
<template>
    <table class="plot-table">
    <tr>
      <td class='key'>Rendition</td>
      <td class='value'>{{rendition}}</td>
    </tr>
    <tr>
      <td class='key'>State</td>
      <td class='value'>{{player_state}}</td>
    </tr>
    <tr>
      <td class='key'>Protection</td>
      <td class='value'>{{protection}}</td>
    </tr>
    <tr>
      <td class='key'>Time to prepare</td>
      <td class='value'>{{time_to_prepare.toFixed(3)}}s</td>
    </tr>
    <tr>
      <td class='key'>UTC</td>
      <td class='value'>
        <table class="inner-table">
          <tr class="inner-table-data">
            <td>{{nowUtc | utcTime}}</td>
            <td>{{live ? positionUtc : '-'}}</td>
            <td>{{live ? timeDelta.toFixed(3) + 's' : '-'}}</td>
            <td></td>
          </tr>
          <tr class="inner-table-descriptions">
            <td>Current UTC</td>
            <td>Playhead UTC</td>
            <td>Behind</td>
            <td></td>
          </tr>
        </table>
      </td>
    </tr>
    <tr v-if="live">
      <td class='key'>
        <table>
          <tr>
            <div title="Playhead in local time">Playhead</div>
          </tr>
        </table>
      </td>
      <td class='value'>{{positionTime}}</td>
    </tr>
    <tr>
      <td class='key'>Buffering</td>
      <td class='value'>
        <table class="inner-table">
          <tr class="inner-table-data">
            <td>{{last_buffering_time.toFixed(3)}}s</td>
            <td>{{total_buffering_time.toFixed(3)}}s</td>
            <td>{{buffering_events}}</td>
            <td></td>
          </tr>
          <tr class="inner-table-descriptions">
            <td>Last</td>
            <td>Sum</td>
            <td>Num</td>
            <td></td>
          </tr>
        </table>
      </td>
    </tr>

    </table>
</template>

<script>
import PlayerComponent from '../PlayerComponent.vue';
import { stateToString } from '../../../utils/player';

export default {
  extends: PlayerComponent,
  name: 'TableStats',
  data () {
    return {
      player_state: 'IDLE',
      last_buffering_time: 0,
      total_buffering_time: 0,
      buffering_events: 0,
      time_to_prepare: 0,
      rendition: '',
      timeDelta: 0,
      positionUtc: 0,
      positionTime: '-',
      nowUtc: 0,
      live: false,
      updateTime: null,
      protection: '-'
    };
  },
  mounted () {
    if (this.updateTime) {
      clearInterval(this.updateTime);
      this.updateTime = null;
    }

    this.updateTime = setInterval(() => {
      this.nowUtc = new Date();
    }, 250);
  },
  destroyed () {
    if (this.updateTime) {
      clearInterval(this.updateTime);
      this.updateTime = null;
    }
  },
  methods: {
    onStateChanged (event) {
      this.player_state = stateToString(event.detail.currentState, event.detail.reason);
      if (event.detail.previousState === clpp.Player.State.PREPARING) {
        this.time_to_prepare = event.detail.timeSinceLastStateChangeMS / 1000.0;
      }
      if (event.detail.currentState === clpp.Player.State.IDLE) {
        this.protection = '-';
        this.last_buffering_time = 0;
        this.total_buffering_time = 0;
        this.buffering_events = 0;
        this.time_to_prepare = 0;
        this.rendition = '';
        this.timeDelta = 0;
        this.positionUtc = 0;
        this.positionTime = '-';
        this.live = false;
      }
      if ((event.detail.currentState === clpp.Player.State.PLAYING || event.detail.currentState === clpp.Player.State.PAUSED) && this.protection === '-') {
        this.protection = 'Clear content';
      }
      this.live = this.ppPlayerRef.get().isLive();
    },
    onBufferingEnded (event) {
      this.last_buffering_time = event.detail.bufferedTimeMS / 1000.0;
      this.total_buffering_time += this.last_buffering_time;
      if (event.detail.reason === clpp.events.BufferingReasons.NO_DATA) {
        this.buffering_events += 1;
      }
    },
    onTimeUpdate () {
      const ppPlayer = this.ppPlayerRef.get();
      const ct = ppPlayer.getPosition();
      const pst = ppPlayer.getPresentationStartTime() ?? 0;
      const utc = new Date((pst + ct) * 1000);
      const now = new Date();
      this.timeDelta = (now - utc) / 1000;
      this.positionUtc = utc.toLocaleString('en-GB', { timeZone: 'UTC' }).split(', ')[1];
      this.positionTime = utc.toLocaleString('en-GB', { dateStyle: 'full', timeStyle: 'long' });
      this.nowUtc = now;
    },
    bps (bps) {
      if (bps) {
        return '@' + clpp.utils.strings.bitrateToString(bps);
      } else {
        return '';
      }
    },
    onBitrateChanged (event) {
      if (event && event.detail) {
        const d = event.detail;
        this.rendition = `${d.width}x${d.height}${this.bps(d.bandwidth)}`;
      } else {
        this.rendition = '';
      }
    },
    onTracksAdded () {
      const videoRendition = this.ppPlayerRef.get().getTrackManager().getVideoRendition();
      if (videoRendition && this.rendition === '') {
        if (videoRendition.width !== null && videoRendition.height !== null) {
          this.rendition = `${videoRendition.width}x${videoRendition.height}${this.bps(videoRendition.bandwidth)}`;
        } else {
          this.rendition = '';
        }
      }
    },
    onDrmSessionUpdate (e) {
      switch (e.keySystem) {
        case 'com.widevine.alpha':
          this.protection = 'Google Widevine';
          break;
        case 'com.microsoft.playready':
        case 'com.microsoft.playready.recommendation':
          this.protection = 'Microsoft Playready';
          break;
        case 'com.apple.fps':
        case 'com.apple.fps.1_0':
        case 'com.apple.fps.2_0':
        case 'com.apple.fps.3_0':
          this.protection = 'Apple Fairplay';
          break;
        default:
          this.protection = 'Protected with ' + e.keySystem;
      }
    },
    async onDrmSessionPersisted (e) {
      const sessionInfo = await clpp.persistent.getSessionStorage().get(e.offlineId);
      if (sessionInfo && sessionInfo.keySystem) {
        this.onDrmSessionUpdate({ keySystem: sessionInfo.keySystem });
      }
    },
    initPlayer (ppPlayerRef) {
      const ppPlayer = ppPlayerRef.get();
      ppPlayer.on(clpp.events.STATE_CHANGED, this.onStateChanged);
      ppPlayer.on(clpp.events.BUFFERING_ENDED, this.onBufferingEnded);
      ppPlayer.on(clpp.events.BITRATE_CHANGED, this.onBitrateChanged);
      ppPlayer.on(clpp.events.TRACKS_ADDED, this.onTracksAdded);
      ppPlayer.on(clpp.events.DRM_SESSION_UPDATE, this.onDrmSessionUpdate);
      ppPlayer.on(clpp.events.DRM_SESSION_PERSISTED, this.onDrmSessionPersisted);
      ppPlayer.on('timeupdate', this.onTimeUpdate);
    },
    disposePlayer (ppPlayerRef) {
      const ppPlayer = ppPlayerRef.get();
      ppPlayer.off(clpp.events.STATE_CHANGED, this.onStateChanged);
      ppPlayer.off(clpp.events.BUFFERING_ENDED, this.onBufferingEnded);
      ppPlayer.off(clpp.events.BITRATE_CHANGED, this.onBitrateChanged);
      ppPlayer.off(clpp.events.TRACKS_ADDED, this.onTracksAdded);
      ppPlayer.off(clpp.events.DRM_SESSION_UPDATE, this.onDrmSessionUpdate);
      ppPlayer.off(clpp.events.DRM_SESSION_PERSISTED, this.onDrmSessionPersisted);
      ppPlayer.off('timeupdate', this.onTimeUpdate);
    }
  }
};
</script>

<style lang="scss" scoped>
  @import "../../../style/color_";
  .key {
    width: 120px;
    min-width: 120px;
    /*text-align: right;*/
    padding-right: 2rem;
  }
  .value {
  }

  .plot-table {
    border-collapse: collapse;
    width: 100%;
    margin-left: 2rem;
  }
  .plot-table tr {
    line-height: 32px;
  }

  .plot-table tr td {

    border: none;
    border-bottom: 1px solid $color-tertiary;
  }
  .plot-table tr:last-child td {
    border-bottom: none;
  }

  .inner-table {
    padding-top: 5px;
    padding-bottom: 5px;
    tr {
      line-height: 20px;
    }
    .inner-table-data td {
      text-align: center;
      min-width: 100px;
    }
    .inner-table-data td:last-child {
      text-align: center;
      min-width: 0;
      width: 100%;
    }
    .inner-table-descriptions td {
      text-align: center;
      text-transform: uppercase;
      font-size: 1rem;
    }
  }

  .inner-table tr td {
    padding-left: 1rem;
    padding-right: 1rem;
    border: none;
  }

  /*.inner-table tr:last-child td {*/
  .inner-table tr td:last-child {
    padding-right: 0;
  }
  .inner-table tr td:first-child {
    padding-left: 0;
  }

</style>
